/**
 * Panel authorization for RubyTill's own staff.
 *
 * FAILS CLOSED. Only the Owner (from /me) sees everything; an error shows a
 * retry screen. Fetches staffApi.me() once per session (retryable) and
 * exposes the caller's section levels ('view' | 'edit'). A staff member gets
 * exactly what their role's levels grant (enforced again server-side by
 * staff-service / admin-access for everything they own); nobody else — not
 * even an unregistered pool member — is granted anything beyond
 * ALWAYS_ALLOWED until /me resolves and says otherwise.
 */

import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { allEdit, allows, type RolePermissions } from '@platform/shared-types';
import { staffApi, StaffMe } from '../services/staffApi';
import { useAuth } from '../contexts/AuthContext';
import { levelForPath } from '../pages/staff/panelPermissions';

interface StaffAccessState {
  loading: boolean;
  error: string | null;
  retry: () => void;
  me: StaffMe | null;
  isOwner: boolean;
  levels: RolePermissions;
  canManage: boolean;
  hasAccess: (path: string) => boolean;
  levelForPath: (path: string) => 'view' | 'edit' | null;
}

const StaffAccessContext = createContext<StaffAccessState>({
  loading: true,
  error: null,
  retry: () => {},
  me: null,
  isOwner: false,
  levels: {},
  canManage: false,
  hasAccess: () => false,
  levelForPath: () => null,
});

export function StaffAccessProvider({ children }: { children: ReactNode }) {
  const { email } = useAuth();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<StaffMe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    staffApi.me()
      .then((res) => { if (!cancelled) setMe(res); })
      .catch((err) => { if (!cancelled) { setMe(null); setError(err?.message || 'unavailable'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attempt, email]);

  const value = useMemo<StaffAccessState>(() => {
    const isOwner = !!me?.isOwner;
    // The owner must never lose a page, including one not in the catalogue
    // at all — allEdit() only covers known sections, so hasAccess below
    // short-circuits to true for the owner on every path.
    const levels: RolePermissions = isOwner ? allEdit() : (me?.levels || {});
    return {
      loading,
      error,
      retry: () => setAttempt((a) => a + 1),
      me,
      isOwner,
      levels,
      canManage: isOwner || allows(levels, '/staff', 'edit'),
      hasAccess: (path: string) => isOwner || !!levelForPath(levels, path),
      levelForPath: (path: string) => (isOwner ? 'edit' : levelForPath(levels, path)),
    };
  }, [loading, error, me]);

  return <StaffAccessContext.Provider value={value}>{children}</StaffAccessContext.Provider>;
}

export function useStaffAccess(): StaffAccessState {
  return useContext(StaffAccessContext);
}
